import cloneDeep from "lodash.clonedeep";
import { AxisDomainItem } from "recharts/types/util/types";

import {
  calculateEstImpact,
  isNoChangeLift,
  TimeSeriesColors,
} from "@intelligems/ig-graphs";
import {
  DataType,
  ExperienceCategory,
  GraphConfig,
  GraphType,
} from "@intelligems/ig-types";
import { officialCategoryName } from "@intelligems/ig-utils";
import { DashboardPageName } from "@src/api/types/dashboards";
import type { BarMetricData, StackedBarMetricData } from "@src/types/analytics";

export const getTextWidth = function (
  isImpactGraph: boolean,
  testGroupLength: number,
  baseWidth: number
) {
  if (isImpactGraph && testGroupLength <= 3) {
    return baseWidth;
  } else if (testGroupLength <= 2) {
    return baseWidth;
  } else if (testGroupLength <= 3) {
    return 65;
  } else if (testGroupLength <= 5) {
    return 55;
  } else {
    return 70;
  }
};

export const getImpact = function (
  groupMetricData: { id: string; value: any; plus_minus?: number | null }[],
  compareGroupA: string,
  compareGroupB: string,
  showUplift?: boolean,
  locale?: string
) {
  const indexA = groupMetricData.findIndex(
    (group) => group.id === compareGroupA
  );
  const indexB = groupMetricData.findIndex(
    (group) => group.id === compareGroupB
  );

  if (groupMetricData[indexB]?.value && groupMetricData[indexA]?.value) {
    const impact = calculateImpact(
      groupMetricData[indexB].value,
      groupMetricData[indexA].value,
      locale
    );
    const uplift = showUplift ? groupMetricData[indexB]?.plus_minus : null;
    if (isNoChangeLift(parseFloat(impact))) {
      return { impact: "No Change", change: "none", uplift };
    }
    return {
      impact,
      change: parseFloat(impact) > 0 ? "increase" : "decrease",
      uplift,
    };
  }
  return { impact: "No Change", change: "none", uplift: null };
};

export const getUplift = function (
  groupMetricData: {
    id: string;
    value: any;
    plus_minus?: number | null;
    uplift?: number;
    uplift_ci_high?: number;
    uplift_ci_low?: number;
  }[],
  compareGroupB: string,
  graphValueFormatter: ({
    isAxisTick,
    value,
    configDataTypes,
    _index,
    decimalAmountShown,
  }: {
    isAxisTick: boolean;
    value: number;
    configDataTypes: DataType[];
    _index?: number;
    decimalAmountShown?: number;
  }) => string | undefined,
  showUplift?: boolean
) {
  const indexB = groupMetricData.findIndex(
    (group) => group.id === compareGroupB
  );

  if (groupMetricData[indexB]?.uplift) {
    const plusMinus = showUplift ? groupMetricData[indexB]?.plus_minus : null;
    const upliftCiHigh = showUplift
      ? groupMetricData[indexB]?.uplift_ci_high
      : null;
    const upliftCiLow = showUplift
      ? groupMetricData[indexB]?.uplift_ci_low
      : null;
    const uplift =
      graphValueFormatter({
        isAxisTick: false,
        value: groupMetricData[indexB].uplift,
        configDataTypes: ["percentTwoDecimal"],
        decimalAmountShown:
          groupMetricData[indexB].uplift * 100 < 10 ? 2 : undefined,
      }) ?? "0";
    if (isNoChangeLift(parseFloat(uplift))) {
      return {
        uplift: "No Change",
        change: "none",
        plusMinus: null,
        upliftCiLow: null,
        upliftCiHigh: null,
      };
    }
    return {
      uplift,
      change: parseFloat(uplift) > 0 ? "increase" : "decrease",
      plusMinus,
      upliftCiHigh,
      upliftCiLow,
    };
  }
  return {
    uplift: "",
    change: "none",
    plusMinus: null,
    upliftCiHigh: null,
    upliftCiLow: null,
  };
};

export const calculateImpact = function (
  valueB: number,
  valueA: number,
  locale?: string
) {
  if (valueB < 0 !== valueA < 0) {
    return "N/A";
  } else {
    return `${new Intl.NumberFormat(locale || "en", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 1,
    }).format(((valueB - valueA) / Math.abs(valueA)) * 100)}%`;
  }
};

export const findTooltip = function (
  metricTitleOrConfig: string | GraphConfig,
  metricTooltips: { [key: string]: string },
  experienceCategory: ExperienceCategory,
  metricFilterIndex?: number
) {
  let metricTitle: string;

  if (typeof metricTitleOrConfig === "string") {
    metricTitle = metricTitleOrConfig;
  } else {
    if (
      !metricTitleOrConfig?.title ||
      !Array.isArray(metricTitleOrConfig.title)
    ) {
      return null;
    }
    metricTitle = metricTitleOrConfig.title[metricFilterIndex || 0];
  }

  const foundTooltip = metricTooltips[metricTitle];

  if (foundTooltip) {
    return foundTooltip.replace(
      "test",
      officialCategoryName(experienceCategory, false)
    );
  } else {
    return null;
  }
};

export { getEstImpactCurrencyMetric } from "@intelligems/ig-graphs";

export const dataMinFormatter = function (dataMin: AxisDomainItem) {
  if (Math.abs(dataMin as number) === 0) {
    return 0;
  } else if (Math.abs(dataMin as number) <= 0.00001) {
    return -0.1;
  } else if (Math.abs(dataMin as number) > 0.00001 && (dataMin as number) < 0) {
    return "auto";
  } else {
    return 0;
  }
};

export const getDataMax = (groupMetricData: BarMetricData[]): number => {
  const valueArray =
    groupMetricData?.map((group) => group.value as number) ?? [];

  return Math.max(...valueArray);
};

export const getDataMin = function (groupMetricData: BarMetricData[]) {
  const valueArray = groupMetricData.map((group) => group.value as number);

  return Math.min(...valueArray);
};

export const getStackedBarRadiusTb = (
  index: number,
  config: GraphConfig
): [number, number, number, number] => {
  if (
    index === config.stackedBarLegendTitles!.length - 1 ||
    config.graphType === GraphType.ClusteredBar
  ) {
    return [3, 3, 0, 0];
  } else {
    return [0, 0, 0, 0];
  }
};

// if it is a clustered graph, do not stack => different stack ids
export const getStackedBarStackId = (index: number, config: GraphConfig) => {
  if (config.graphType === GraphType.ClusteredBar) {
    return `value${index + 1}`;
  } else {
    return "stackId";
  }
};

export const stackedDataMaxFormatter = (
  config: GraphConfig,
  dataMax: number
) => {
  if (config.dataTypes[0] === "percent") {
    return "dataMax";
  } else {
    return "auto";
  }
};

export const getStackedDataMax = (
  groupMetricData: StackedBarMetricData[],
  config: GraphConfig
) => {
  const valueArray = groupMetricData.map((data) =>
    (config.stackedBarLegendTitles || []).reduce(
      (prev, curr) => prev + (data[curr] as number),
      0
    )
  );

  return Math.max(...valueArray);
};

export const getStackedDataValue = (
  config: GraphConfig,
  dataValue1: number | undefined,
  dataValue2: number | undefined
) => {
  if (
    config.showSumStackedLabels ||
    config.graphType === GraphType.ClusteredBar
  ) {
    return dataValue1 ? dataValue1 : 0;
  } else {
    const percentage =
      (dataValue1 ? dataValue1 : 0) /
      ((dataValue1 ? dataValue1 : 0) + (dataValue2 ? dataValue2 : 0));
    return percentage || 0;
  }
};

export const valueAccessor = (
  entry: any,
  index: number,
  config: GraphConfig,
  graphValueFormatter: ({
    isAxisTick,
    value,
    configDataTypes,
    _index,
    decimalAmountShown,
  }: {
    isAxisTick: boolean;
    value: number;
    configDataTypes: DataType[];
    _index?: number;
    decimalAmountShown?: number;
  }) => string | undefined
) => {
  if (index === config.stackedBarLegendTitles!.length - 1) {
    return entry
      ? graphValueFormatter({
          isAxisTick: false,
          value: entry.value[entry.value.length - 1],
          configDataTypes: config.dataTypes,
        })
      : 0;
  } else {
    return null;
  }
};

export const calculateEstImpactTb = (
  config: GraphConfig,
  visitors: number,
  baseValue: number,
  compareValue: number,
  storeCurrencyConfig: { code: string; locale: string },
  testDuration: number,
  pageName: DashboardPageName
): string => {
  const metricSuffix = pageName === "customMetrics" ? "OCCURRENCES" : undefined;

  return calculateEstImpact(
    config.dataTypes,
    visitors,
    baseValue,
    compareValue,
    storeCurrencyConfig,
    testDuration,
    metricSuffix,
    config?.title?.[0]
  );
};

export const getFilterMethod = (
  data: BarMetricData[],
  filterMethod: string | undefined
) => {
  const groupData: BarMetricData[] = [];
  const dataCopy = cloneDeep(data);

  dataCopy.forEach((data) => {
    if (data.filterMethod === filterMethod) {
      groupData.push(data);
    }
  });

  return groupData;
};

export function removePrefix(text: string) {
  // Find the position of the first '/'
  const slashPos = text.indexOf("/");
  // Return everything after the slash
  if (slashPos !== -1) {
    return text.substring(slashPos + 1);
  } else {
    return text; // No slash found, return original string
  }
}

export const generateDistinctGraphColors = (count: number): string[] => {
  if (count <= TimeSeriesColors.length) {
    return TimeSeriesColors.slice(0, count);
  }

  const colors = [...TimeSeriesColors];
  const hueStep = 360 / count;

  for (let i = TimeSeriesColors.length; i < count; i++) {
    const hue = (i * hueStep) % 360;
    const saturation = 60 + (i % 3) * 10; // Vary saturation
    const lightness = 45 + (i % 4) * 5; // Vary lightness
    colors.push(hslToHex(hue, saturation, lightness));
  }

  return colors;
};

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
